<template>
  <ReturnConfirmationShell
    v-if="data"
    :current-page-title="i18n(translations.pageTitle)"
    data-test="express-replacement-confirmation-page"
    hide-link-to-my-orders
    :resolution="data.resolution"
  >
    <template #subHeading>
      {{ i18n(translations.subHeading) }}
    </template>

    <template #heading>
      {{ i18n(translations.headingExpressReplacement) }}
    </template>

    <template #description>
      {{
        i18n(translations.description, {
          min: deliveryDates?.min,
          max: deliveryDates?.max,
        })
      }}
    </template>

    <template #actions>
      <PrintLabelAction
        v-if="shipmentUrl"
        :shipment-label-url="shipmentUrl"
        variant="primary"
      />
      <ProofOfPurchaseAction v-if="orderPublicId" :order-public-id />
      <GoToSelectYourOrderlineAction
        v-if="isCustomerEligibleToGetMoreHelp"
        :orderline-public-id="data.resolution.problemContext.orderline.publicId"
      />
    </template>
    <template #content>
      <ExpressReplacementConfirmationNextSteps :delivery-dates :return-window />
    </template>
  </ReturnConfirmationShell>
</template>

<script lang="ts" setup>
import { navigateTo, useAsyncData, useRoute } from '#imports'
import { computed } from 'vue'

import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import ExpressReplacementConfirmationNextSteps from '~/scopes/customer-care/resolution/confirmationPages/express-replacement/ExpressReplacementConfirmationNextSteps.vue'
import GoToSelectYourOrderlineAction from '~/scopes/customer-care/resolution/confirmationPages/return/components/Actions/GoToSelectYourOrderlineAction/GoToSelectYourOrderlineAction.vue'
import PrintLabelAction from '~/scopes/customer-care/resolution/confirmationPages/return/components/Actions/PrintLabelAction/PrintLabelAction.vue'
import ProofOfPurchaseAction from '~/scopes/customer-care/resolution/confirmationPages/return/components/Actions/ProofOfPurchaseAction/ProofOfPurchaseAction.vue'
import ReturnConfirmationShell from '~/scopes/customer-care/resolution/confirmationPages/return/components/ReturnConfirmationShell/ReturnConfirmationShell.vue'
import { CUSTOMER_CARE } from '~/scopes/customer-care/route-names'
import { ROUTE_NAMES as DASHBOARD_ROUTE } from '~/scopes/dashboard/constants'

import translations from './ExpressReplacementConfirmation.translations'

const { params } = useRoute()
const i18n = useI18n()
const $httpFetch = useHttpFetch()

const getResolutionXrConfirmationKey = getAsyncDataKey(
  'express-replacement-confirmation-get-resolution',
  {
    resolutionId: params.resolutionId,
  },
)

const { data, error } = await useAsyncData(getResolutionXrConfirmationKey, () =>
  $httpFetch('/resolution-engine/resolutions/:resolutionId', {
    pathParams: {
      resolutionId: String(params.resolutionId),
    },
  }),
)
if (error.value) {
  await navigateTo({
    name: DASHBOARD_ROUTE.DASHBOARD.MY_ORDERS.SELF,
  })
}
if (!data.value?.resolution.resolutionRequest.extraData) {
  await navigateTo({
    name: CUSTOMER_CARE.RESOLUTION.RESOLUTION_CONTACT_CONFIRMATION,
    params: { resolutionId: params.resolutionId },
  })
}

const isCustomerEligibleToGetMoreHelp = computed(() => {
  return (
    data.value?.resolution.state !== 'INITIATED' &&
    data.value?.resolution.state !== 'FAILED'
  )
})

const deliveryDates = computed(() => {
  return {
    min: new Date(
      data.value?.resolution.resolutionRequest.extraData?.xrDeliveryDateMin ??
        '',
    ),
    max: new Date(
      data.value?.resolution.resolutionRequest.extraData?.xrDeliveryDateMax ??
        '',
    ),
  }
})

const returnWindow = computed(
  () =>
    new Date(
      data.value?.resolution.resolutionRequest.extraData?.xrReturnWindow ?? '',
    ),
)

const shipmentUrl = computed(
  () => data.value?.resolution.resolutionRequest.shipment?.labelUrl,
)
const orderPublicId = computed(
  () => data.value?.resolution.problemContext.orderline.publicId,
)
</script>
